import React, { useState, useEffect, useRef } from 'react';
import { useGlobalContext } from '../../js/context';
import Button from '../button/Button.jsx';
import Tooltip from '../tooltip/Tooltip.jsx';
import Sidebar from './Sidebar.jsx';
import ElementPageAnime from './ElementPageAnime';
import anime from 'animejs/lib/anime.es.js';
import Video from '../video/Video.jsx';
import './ElementPage.scss';
import ReactGA from 'react-ga4';

const DetailPage = () => {
	const [active, setActive] = useState('first');
	const [videoSource, setVideoSource] = useState();
	const { singleProduct, products, isSafari, prevPage } = useGlobalContext();
	const reductionPercentRef = useRef();
	const videoRef = useRef();

	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: singleProduct?.name,
			title: singleProduct?.name,
		});
	}, [singleProduct]);

	useEffect(() => {
		ElementPageAnime(singleProduct?.reduction_percent);
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		setVideoSource(
			isSafari
				? `https://www.pmi.com/resources/tools-for-science/videos/particles-safari-${singleProduct?.reduction_quantity}.mp4`
				: `https://www.pmi.com/resources/tools-for-science/videos/particles-chrome-${singleProduct?.reduction_quantity}.webm`
		);
		//eslint-disable-next-line
	}, [singleProduct?.reduction_percent]);

	useEffect(() => {
		videoRef.current.setAttribute('src', videoSource);
	}, [videoSource]);

	useEffect(() => {
		if (prevPage === 'element_page') {
			anime({
				targets: reductionPercentRef.current,
				innerHTML: [0.0, singleProduct?.reduction_percent],
				easing: 'easeInOutExpo',
				round: 10,
				duration: 5000,
				delay: -750,
				opacity: [0, 1],
				complete: function () {
					if (reductionPercentRef && reductionPercentRef.current) {
						reductionPercentRef.current.innerText =
							singleProduct?.reduction_percent;
					}
				},
			});
		}
	}, [singleProduct?.reduction_percent, prevPage]);

	const additionalInfoText = (
		<p>
			{`The measured concentration of ${singleProduct?.name} is below the limit of quantification, but we can say that it is reduced by more than ${singleProduct?.reduction_percent}% compared to a cigarette.`}
		</p>
	);

	//eslint-disable-next-line
	const checkIfReductionIsOne = singleProduct?.reduction_quantity == 1;

	return (
		<div className='tos-element tos-hide-before-animated'>
			<Sidebar />

			<div className='tos-element__content'>
				<span className='tos-element__pagination tos-hide-before-animated'>
					<strong>{singleProduct?.id + 1}</strong> / {products.length}
				</span>
				<h1 className='tos-element__title tos-font-big'>
					{singleProduct?.name}
				</h1>

				{singleProduct?.sub_title && (
					<span
						className='tos-element__subtitle'
						dangerouslySetInnerHTML={{
							__html: singleProduct?.sub_title,
						}}
					/>
				)}
				<div className='tos-element__buttons tos-hide-before-animated'>
					<Button
						title='CIGARETTE'
						secondary={true}
						active={`${active === 'first'}`}
						onClick={() => setActive('first')}
					/>
					<Button
						title='OUR HEATED TOBACCO PRODUCT'
						active={`${active === 'second'}`}
						secondary={true}
						onClick={() => setActive('second')}
					/>
				</div>
				<div className='tos-element__top'>
					<div
						className={`tos-element__top-text tos-element__top-text--left ${
							active === 'first' ? 'tos-element__top-text--active' : ''
						}`}
					>
						<h2>A cigarette</h2>
					</div>

					<div className='tos-element__separator'>
						<div className='tos-element__separator-line tos-element__separator-line--left'></div>
						<span>VS</span>
						<div className='tos-element__separator-line tos-element__separator-line--right'></div>
						<div className='tos-element__separator-line tos-element__separator-bottom-line'></div>
					</div>

					<div
						className={`tos-element__top-text tos-element__top-text--right ${
							active === 'second' ? 'tos-element__top-text--active' : ''
						}`}
					>
						<h2>Our heated tobacco product</h2>
					</div>
				</div>

				<div className='tos-element__middle-wrapper'>
					<Tooltip
						className={`cmp-element__tooltip tos-element__tooltip--left tos-hide-before-animated ${
							active === 'first' ? 'cmp-element__tooltip--active' : ''
						}`}
						direction='right'
						iconType={'image'}
					>
						<p>
							The 100 dots represent 100% of the constituent in the smoke of the
							reference cigarette 3R4F.
						</p>
					</Tooltip>
					<div
						className={`tos-element-video tos-element-video--left ${
							active === 'first' ? 'tos-element-video--active' : ''
						}`}
					>
						<Video
							videoSource={
								isSafari
									? 'https://www.pmi.com/resources/tools-for-science/videos/particles-safari-100.mp4'
									: 'https://www.pmi.com/resources/tools-for-science/videos/particles-chrome-100.webm'
							}
							videoType={isSafari ? 'mp4' : 'webm'}
						/>
					</div>

					<div className='tos-element__middle tos-hide-before-animated tos-element__middle--no-info'>
						{singleProduct?.has_additional_info && (
							<Tooltip
								className='tos-element__middle__tooltip tos-element__tooltip--right'
								iconType={'svg'}
							>
								{additionalInfoText}
							</Tooltip>
						)}

						<div className='tos-element__middle-num'>
							{singleProduct?.percentage_text && (
								<span className='tos-element__middle-top'>
									{singleProduct?.percentage_text}
								</span>
							)}
							<h3 className='tos-element__middle-title'>
								<strong
									className='tos-element__middle-reduction tos-hide-before-animated'
									ref={reductionPercentRef}
								>
									{singleProduct?.reduction_percent}
								</strong>
								<span className='tos-element__middle-percent tos-hide-before-animated'>
									%
								</span>
								<div className='tos-element__middle-reduction-text'>
									reduction
								</div>
							</h3>
						</div>
					</div>

					<div
						className={`tos-element-video tos-element-video--right ${
							active === 'second' ? 'tos-element-video--active' : ''
						}`}
					>
						<Video
							videoSource={videoSource}
							videoType={isSafari ? 'mp4' : 'webm'}
							ref={videoRef}
						/>
					</div>
					<Tooltip
						className={`cmp-element__tooltip cmp-element__tooltip--right tos-hide-before-animated ${
							active === 'second' ? 'cmp-element__tooltip--active' : ''
						}`}
						direction='left'
						iconType={'image'}
					>
						<p>
							{`The ${singleProduct?.reduction_quantity} 
              ${checkIfReductionIsOne ? 'dot' : 'dots'} 
              ${
								checkIfReductionIsOne ? 'represents' : 'represent'
							} the percentage of the constituent in the aerosol of our heated tobacco product relative to the smoke of the reference cigarette 3R4F.`}
						</p>
					</Tooltip>
				</div>

				<div className='tos-element__bottom'>
					<div
						className={`tos-element__bottom-text tos-hide-before-animated ${
							active === 'first' ? 'tos-element__bottom-text--active' : ''
						}`}
					>
						<div className='tos-element__bottom-num tos-hide-before-animated'>
							{singleProduct?.less_than_cigarette && (
								<span className='tos-element__bottom-left'>LESS THAN</span>
							)}
							<h3 className='tos-element__bottom-title'>
								{singleProduct?.cigarette_percent}{' '}
								<span className='tos-element__bottom-text-small'>
									{singleProduct?.unit}
								</span>
							</h3>
						</div>
						<span className='tos-element__bottom-text-xsmall tos-hide-before-animated'>
							per cigarette
						</span>
					</div>

					<div
						className={`tos-element__bottom-text tos-hide-before-animated ${
							active === 'second' ? 'tos-element__bottom-text--active' : ''
						}`}
					>
						<div className='tos-element__bottom-num tos-hide-before-animated'>
							{singleProduct?.less_than_tabacco && (
								<span className='tos-element__bottom-left'>LESS THAN</span>
							)}
							<h3 className='tos-element__bottom-title'>
								{singleProduct?.tabacco_percent}
								<span className='tos-element__bottom-text-small'>
									{' '}
									{singleProduct?.unit}
								</span>
							</h3>
						</div>
						<span className='tos-element__bottom-text-xsmall tos-hide-before-animated'>
							per heated tobacco product
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DetailPage;
